import type { DtoolsReviewPrimary, DtoolsReviewSystem } from "./dtoolsStagingTransform";
import { productPrimaryBlocks } from "./dtoolsReviewNavigation";
import { inferDtoolsEquipmentRole } from "./dtoolsEquipmentRoles";

export type DtoolsQuotedItemSummary = {
  manufacturer: string | null;
  model: string | null;
  category: string | null;
  description: string;
  quantity: number;
  lineTotal: number;
  isAccessory?: boolean;
  equipmentRole?: string;
};

function summarizeLine(
  line: {
    manufacturer: string | null;
    model: string | null;
    category: string | null;
    polishedDescription: string;
    dtoolsDescription: string;
    quantity: number;
    lineTotal: number;
    bucket?: string | null;
    isAccessory?: boolean;
    subcategory?: string | null;
  },
  isAccessory = false,
): DtoolsQuotedItemSummary {
  const description =
    String(line.polishedDescription ?? "").trim() ||
    String(line.dtoolsDescription ?? "").trim() ||
    [line.manufacturer, line.model].filter(Boolean).join(" ").trim() ||
    "Item";
  return {
    manufacturer: line.manufacturer,
    model: line.model,
    category: line.category,
    description: description.slice(0, 500),
    quantity: Math.max(1, Math.round(line.quantity || 1)),
    lineTotal: Math.max(0, Number(line.lineTotal) || 0),
    isAccessory,
    equipmentRole: inferDtoolsEquipmentRole(line),
  };
}

function collectFromPrimaries(
  blocks: DtoolsReviewPrimary[],
  options?: { includeAccessories?: boolean },
): DtoolsQuotedItemSummary[] {
  const includeAccessories = options?.includeAccessories !== false;
  const out: DtoolsQuotedItemSummary[] = [];
  for (const block of productPrimaryBlocks(blocks)) {
    out.push(summarizeLine(block.line, false));
    if (includeAccessories) {
      for (const accessory of block.accessories) {
        out.push(summarizeLine(accessory, true));
      }
    }
  }
  return out;
}

/** Customer-facing quoted products/services inside one option/system (for AI overview generation). */
export function collectQuotedItemsForSystem(system: DtoolsReviewSystem): DtoolsQuotedItemSummary[] {
  const items: DtoolsQuotedItemSummary[] = [];
  for (const room of system.rooms) {
    items.push(...collectFromPrimaries(room.primaries));
  }
  items.push(...collectFromPrimaries(system.systemLevelPrimaries));
  return items;
}

/** Primary equipment only — accessories de-prioritised for AI overview briefs. */
export function collectPrimaryQuotedItemsForOverview(system: DtoolsReviewSystem): DtoolsQuotedItemSummary[] {
  const items: DtoolsQuotedItemSummary[] = [];
  for (const room of system.rooms) {
    items.push(...collectFromPrimaries(room.primaries, { includeAccessories: false }));
  }
  items.push(...collectFromPrimaries(system.systemLevelPrimaries, { includeAccessories: false }));
  return items;
}

export function buildSystemOverviewGeneratePayload(input: {
  optionName: string;
  roomNames?: string[];
  systemTotal?: number;
  items: DtoolsQuotedItemSummary[];
}): string {
  const primaryItems = input.items.filter((item) => !item.isAccessory);
  const accessoryItems = input.items.filter((item) => item.isAccessory);
  const focusItems = primaryItems.length > 0 ? primaryItems : input.items;

  const itemLines = focusItems.slice(0, 80).map((item) => {
    const role = item.equipmentRole ?? "Equipment";
    const brandModel = [item.manufacturer, item.model].filter(Boolean).join(" ").trim();
    const qty = item.quantity > 1 ? ` ×${item.quantity}` : "";
    const label = brandModel || item.description;
    const detail = item.description && brandModel && item.description !== brandModel ? ` — ${item.description}` : "";
    return `- ${role}: ${label}${detail}${qty}`;
  });

  const optionLabel = String(input.optionName ?? "").trim();
  const bareOptionNumber = /^option\s+\d+$/i.test(optionLabel);
  const accessoryNote =
    accessoryItems.length > 0
      ? `Supporting component lines (${accessoryItems.length}) are omitted — mention only if essential to the system story.`
      : "";

  return [
    bareOptionNumber
      ? `Option reference: ${optionLabel} (no custom label yet — avoid starting prose with this unless necessary)`
      : `Option / system label: ${optionLabel} (use this name in prose — do NOT prepend "Option 1")`,
    "Identify the actual system type from the quoted items below (lighting control, cinema, audio, networking, etc.).",
    "Write a specific project summary: what the system is, how major components work together, and the practical homeowner benefit.",
    "Do NOT use generic filler such as 'complete performance system' or 'engineered for immersive results'.",
    "Do NOT centre the overview on room names or layout.",
    "Do NOT simply list model numbers — explain the system story.",
    "Do NOT invent specifications or change brand/model names.",
    input.systemTotal != null && input.systemTotal > 0 ? `Option total (SGD): ${input.systemTotal.toFixed(2)}` : "",
    accessoryNote,
    "Primary quoted equipment by role:",
    ...(itemLines.length ? itemLines : ["- (No billable items listed)"]),
  ]
    .filter(Boolean)
    .join("\n");
}

/** Remove legacy room-list blocks from AI or template overview copy. */
export function stripRoomsIncludedFromOverview(text: string): string {
  let out = String(text ?? "").trim();
  if (!out) return out;
  out = out.replace(/\n?\s*rooms?\s+included\s*\n[\s\S]*?(?=\n\n|\n[A-Z]|$)/gi, "").trim();
  out = out.replace(/\n?\s*ROOMS?\s+INCLUDED\s*\n[\s\S]*?(?=\n\n|\n[A-Z]|$)/g, "").trim();
  return out;
}
